import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { tenantApiFetch, getTenantToken, setTenantToken } from '../tenant/tenantApi';

const TenantAuthContext = createContext(null);

export function TenantAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [ready, setReady] = useState(false);

  const refreshMe = useCallback(async () => {
    const token = getTenantToken();
    if (!token) {
      setUser(null);
      setTenant(null);
      setPermissions([]);
      setReady(true);
      return;
    }
    try {
      const res = await tenantApiFetch('/api/tenant/auth/me');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTenantToken(null);
        setUser(null);
        setTenant(null);
        setPermissions([]);
        setReady(true);
        return;
      }
      setUser(data.user);
      setTenant(data.tenant || null);
      setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
    } catch (_e) {
      setTenantToken(null);
      setUser(null);
      setTenant(null);
      setPermissions([]);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async ({ tenantSlug, email, password }) => {
    const res = await tenantApiFetch('/api/tenant/auth/login', {
      method: 'POST',
      body: { tenantSlug, email, password }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data.error || data.message || 'Login failed';
      const err = new Error(msg);
      if (data.code) err.code = data.code;
      throw err;
    }
    setTenantToken(data.token);
    setUser(data.user);
    setTenant(data.user.tenant);
    setPermissions(data.user.permissions || []);
    setReady(true);
  }, []);

  const logout = useCallback(() => {
    setTenantToken(null);
    setUser(null);
    setTenant(null);
    setPermissions([]);
  }, []);

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  const hasPermission = useCallback((code) => permissionSet.has(code), [permissionSet]);

  const value = useMemo(
    () => ({
      user,
      tenant,
      permissions,
      ready,
      login,
      logout,
      refreshMe,
      hasPermission
    }),
    [user, tenant, permissions, ready, login, logout, refreshMe, hasPermission]
  );

  return <TenantAuthContext.Provider value={value}>{children}</TenantAuthContext.Provider>;
}

export function useTenantAuth() {
  const ctx = useContext(TenantAuthContext);
  if (!ctx) {
    throw new Error('useTenantAuth must be used within TenantAuthProvider');
  }
  return ctx;
}
